import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useTenant } from '@/contexts/TenantContext';
import { MarketingSettings } from './useMarketingSettings';

interface ConsentState {
  hasConsented: boolean;
  consentGiven: boolean;
}

export const useMarketingScripts = () => {
  const [settings, setSettings] = useState<MarketingSettings | null>(null);
  const [consentState, setConsentState] = useState<ConsentState>({
    hasConsented: false,
    consentGiven: false
  });
  const [loading, setLoading] = useState(true);
  const { tenant } = useTenant();

  // Carregar configurações de marketing
  const loadMarketingSettings = async () => {
    if (!tenant) return;

    try {
      const { data, error } = await supabase
        .from('marketing_settings')
        .select('*')
        .eq('tenant_id', tenant.id)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') {
        console.error('Erro ao carregar configurações de marketing:', error);
        return;
      }

      if (data) {
        setSettings(data as MarketingSettings);
      }
    } catch (error) {
      console.error('Erro ao carregar configurações de marketing:', error);
    } finally {
      setLoading(false);
    }
  };

  // Verificar consentimento salvo
  const checkConsent = () => {
    const savedConsent = localStorage.getItem('marketing-consent');
    if (savedConsent) {
      const consent = JSON.parse(savedConsent);
      setConsentState({
        hasConsented: true,
        consentGiven: consent.given
      });
    }
  };

  // Salvar consentimento
  const saveConsent = (given: boolean) => {
    const consent = {
      given,
      timestamp: new Date().toISOString()
    };
    localStorage.setItem('marketing-consent', JSON.stringify(consent));
    setConsentState({
      hasConsented: true,
      consentGiven: given
    });
  };

  // Injetar Google Analytics 4
  const injectGA4 = (measurementId: string) => {
    if (typeof window === 'undefined') return;

    // Verificar se já foi injetado
    if (window.gtag || document.querySelector(`script[src*="${measurementId}"]`)) return;

    // Validar ID antes de usar
    if (!measurementId.match(/^G-[A-Z0-9]{10}$/)) {
      console.error('ID de medição do Google Analytics inválido:', measurementId);
      return;
    }

    // Carregar script do Google Analytics
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(measurementId)}`;
    script.setAttribute('data-marketing-script', 'ga4');
    document.head.appendChild(script);

    // Configurar gtag
    window.dataLayer = window.dataLayer || [];
    function gtag(...args: any[]) {
      window.dataLayer.push(args);
    }
    window.gtag = gtag;

    gtag('js', new Date());
    gtag('config', measurementId, {
      page_title: document.title,
      page_location: window.location.href
    });

    console.log('Google Analytics 4 carregado:', measurementId);
  };

  // Injetar Facebook Pixel
  const injectFacebookPixel = (pixelId: string) => {
    if (typeof window === 'undefined') return;

    // Verificar se já foi injetado
    if (window.fbq || document.querySelector(`script[data-marketing-script="facebook-pixel"]`)) return;

    // Validar ID antes de usar
    if (!pixelId.match(/^\d{15,16}$/)) {
      console.error('ID do Facebook Pixel inválido:', pixelId);
      return;
    }

    // Carregar script do Facebook Pixel de forma segura
    const script = document.createElement('script');
    script.setAttribute('data-marketing-script', 'facebook-pixel');
    
    // Usar textContent em vez de innerHTML para evitar XSS
    script.textContent = `
      !function(f,b,e,v,n,t,s)
      {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
      n.callMethod.apply(n,arguments):n.queue.push(arguments)};
      if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
      n.queue=[];t=b.createElement(e);t.async=!0;
      t.src=v;s=b.getElementsByTagName(e)[0];
      s.parentNode.insertBefore(t,s)}(window, document,'script',
      'https://connect.facebook.net/en_US/fbevents.js');
      fbq('init', '${pixelId}');
      fbq('track', 'PageView');
    `;
    document.head.appendChild(script);

    // Adicionar noscript para casos sem JavaScript
    const noscript = document.createElement('noscript');
    const img = document.createElement('img');
    img.height = 1;
    img.width = 1;
    img.style.display = 'none';
    img.src = `https://www.facebook.com/tr?id=${encodeURIComponent(pixelId)}&ev=PageView&noscript=1`;
    noscript.appendChild(img);
    document.head.appendChild(noscript);

    console.log('Facebook Pixel carregado:', pixelId);
  };

  // Injetar Google Tag Manager
  const injectGTM = (containerId: string) => {
    if (typeof window === 'undefined') return;

    // Verificar se já foi injetado
    if (window.dataLayer && window.dataLayer.find((item: any) => item.gtm_id) || 
        document.querySelector(`script[data-marketing-script="gtm"]`)) return;

    // Validar ID antes de usar
    if (!containerId.match(/^GTM-[A-Z0-9]{6,7}$/)) {
      console.error('ID do container do Google Tag Manager inválido:', containerId);
      return;
    }

    // Script do GTM no head
    const gtmScript = document.createElement('script');
    gtmScript.setAttribute('data-marketing-script', 'gtm');
    gtmScript.textContent = `
      (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
      new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
      j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
      'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
      })(window,document,'script','dataLayer','${containerId}');
    `;
    document.head.appendChild(gtmScript);

    // Noscript do GTM no body
    const gtmNoscript = document.createElement('noscript');
    const iframe = document.createElement('iframe');
    iframe.src = `https://www.googletagmanager.com/ns.html?id=${encodeURIComponent(containerId)}`;
    iframe.height = '0' as any;
    iframe.width = '0' as any;
    iframe.style.display = 'none';
    iframe.style.visibility = 'hidden';
    gtmNoscript.appendChild(iframe);
    document.body.insertBefore(gtmNoscript, document.body.firstChild);

    console.log('Google Tag Manager carregado:', containerId);
  };

  // Injetar Google Ads
  const injectGoogleAds = (conversionId: string, conversionLabel: string) => {
    if (typeof window === 'undefined') return;

    // Validar formato do ID antes de usar
    if (!conversionId || !conversionId.match(/^[0-9]{8,12}$/)) {
      console.error('ID de conversão do Google Ads inválido:', conversionId);
      return;
    }

    const safeConversionId = encodeURIComponent(conversionId.trim());

    // Carregar script do Google Ads
    const script = document.createElement('script');
    script.src = `https://www.googletagmanager.com/gtag/js?id=AW-${safeConversionId}`;
    script.async = true;
    script.setAttribute('data-marketing-script', 'google-ads');
    document.head.appendChild(script);

    // Configurar gtag para Google Ads
    window.dataLayer = window.dataLayer || [];
    function gtag(...args: any[]) {
      window.dataLayer.push(args);
    }
    window.gtag = window.gtag || gtag;

    gtag('js', new Date());
    gtag('config', `AW-${safeConversionId}`);

    console.log('Google Ads carregado:', conversionId, conversionLabel);
  };

  // Rastrear eventos personalizados
  const trackEvent = (eventName: string, parameters?: any) => {
    if (!consentState.consentGiven) return;

    // Google Analytics 4
    if (window.gtag && settings?.ga4_enabled) {
      window.gtag('event', eventName, parameters);
    }

    // Facebook Pixel
    if (window.fbq && settings?.facebook_pixel_enabled) {
      window.fbq('track', eventName, parameters);
    }

    // Google Tag Manager
    if (window.dataLayer && settings?.gtm_enabled) {
      window.dataLayer.push({
        event: eventName,
        ...parameters
      });
    }

    console.log('Evento rastreado:', eventName, parameters);
  };

  // Rastrear visualização de imóvel
  const trackPropertyView = (propertyId: string, propertyTitle: string, propertyPrice?: number) => {
    trackEvent('view_item', {
      item_id: propertyId,
      item_name: propertyTitle,
      value: propertyPrice,
      currency: 'BRL',
      item_category: 'real_estate'
    });
  };

  // Rastrear clique em contato
  const trackContactClick = (propertyId: string, contactMethod: string) => {
    trackEvent('contact_click', {
      item_id: propertyId,
      contact_method: contactMethod
    });
  };

  // Rastrear envio de formulário
  const trackFormSubmit = (formType: string, propertyId?: string) => {
    trackEvent('form_submit', {
      form_type: formType,
      item_id: propertyId
    });
  };

  // Carregar scripts baseado nas configurações
  useEffect(() => {
    if (!settings || !consentState.consentGiven) return;

    // Google Analytics 4
    if (settings.ga4_enabled && settings.ga4_measurement_id) {
      injectGA4(settings.ga4_measurement_id);
    }

    // Facebook Pixel
    if (settings.facebook_pixel_enabled && settings.facebook_pixel_id) {
      injectFacebookPixel(settings.facebook_pixel_id);
    }

    // Google Tag Manager
    if (settings.gtm_enabled && settings.gtm_container_id) {
      injectGTM(settings.gtm_container_id);
    }

    // Google Ads
    if (settings.google_ads_enabled && settings.google_ads_conversion_id && settings.google_ads_conversion_label) {
      injectGoogleAds(settings.google_ads_conversion_id, settings.google_ads_conversion_label);
    }
  }, [settings, consentState.consentGiven]);

  // Carregar configurações e verificar consentimento
  useEffect(() => {
    loadMarketingSettings();
    checkConsent();
  }, [tenant]);

  return {
    settings,
    consentState,
    loading,
    saveConsent,
    trackEvent,
    trackPropertyView,
    trackContactClick,
    trackFormSubmit
  };
};

// Declarações de tipos globais
declare global {
  interface Window {
    gtag: (...args: any[]) => void;
    fbq: (...args: any[]) => void;
    dataLayer: any[];
  }
}
